// The comments column beside a document (#13194, artboard 2), and at phone
// the same list as a bottom sheet (artboard 5): the header with the open
// count, a filter (Open, Resolved, All), and the thread cards in creation
// order, resolved ones under a collapsed "Resolved" row.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "@mui/material";
import { ChevronDown, CircleCheck, ListFilter, X } from "lucide-react";
import { Menu } from "@/components/FpPages/RMS/design";
import useDismissOnBack from "@/hooks/useDismissOnBack";
import DocumentCommentThread, {
  COMMENT_EDIT_FIELD_ATTR,
} from "./DocumentCommentThread";
import DocumentCommentComposer from "./DocumentCommentComposer";
import {
  COMMENTS_UI_ATTR,
  type CommentDraft,
} from "../FpEditor/services/documentCommentsBridge";
import type {
  DocumentCommentThread as Thread,
  UseDocumentComments,
} from "./documentComments";
import type { MarginCardPlacement } from "./useDocumentCommentsHost";

/** The column's width beside the document (artboard 2). */
export const COMMENTS_COLUMN_PX = 312;

type Filter = "open" | "resolved" | "all";

const FILTER_LABELS: Record<Filter, string> = {
  open: "Open",
  resolved: "Resolved",
  all: "All",
};

export interface DocumentCommentsPanelProps {
  artifactId: string;
  comments: UseDocumentComments;
  /** Whether the live document still carries a thread's mark. */
  inDocument: (thread: Thread) => boolean;
  activeThreadId: string | null;
  onActivate: (threadId: string) => void;
  /** A comment being written on a selection, shown at the top while open. */
  draft: CommentDraft | null;
  onCancelDraft: () => void;
  /** Post the draft, sent to the document's agent or not. */
  onSubmitDraft: (body: string, sendToAgent: boolean) => Promise<unknown>;
  presentation: "column" | "sheet";
  onClose: () => void;
}

const IconButton: React.FC<{
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  expanded?: boolean;
}> = ({ label, onClick, children, expanded }) => (
  <Tooltip title={label} arrow placement="bottom" describeChild>
    <button
      type="button"
      aria-label={label}
      aria-haspopup={expanded === undefined ? undefined : "menu"}
      aria-expanded={expanded}
      onClick={onClick}
      className="inline-flex h-7 w-7 flex-none cursor-pointer items-center justify-center rounded-[6px] text-[#5a6068] hover:bg-[var(--fp-bg-well)] hover:text-[var(--fp-ink)]"
    >
      {children}
    </button>
  </Tooltip>
);

const PanelBody: React.FC<
  DocumentCommentsPanelProps & { withFilter: boolean }
> = ({
  artifactId,
  comments,
  inDocument,
  activeThreadId,
  onActivate,
  draft,
  onCancelDraft,
  onSubmitDraft,
  presentation,
  onClose,
  withFilter,
}) => {
  const [filter, setFilter] = useState<Filter>("open");
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [resolvedOpen, setResolvedOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const open = comments.threads.filter((t) => t.resolved_at === null);
  const resolved = comments.threads.filter((t) => t.resolved_at !== null);

  // A thread made active while resolved (a link can name one) shows under
  // its expanded row. Only on the change of active thread: resolving the
  // active card folds it away with the rest.
  const shownActive = useRef<string | null>(null);
  useEffect(() => {
    if (activeThreadId === shownActive.current) return;
    shownActive.current = activeThreadId;
    if (resolved.some((t) => t.id === activeThreadId)) setResolvedOpen(true);
  }, [activeThreadId, resolved]);

  // The active card in view: a click on its highlight brings it here.
  useEffect(() => {
    if (!activeThreadId) return;
    const card = listRef.current?.querySelector<HTMLElement>(
      `[data-thread-id="${CSS.escape(activeThreadId)}"]`
    );
    card?.scrollIntoView({ block: "nearest" });
  }, [activeThreadId, resolvedOpen, filter]);

  const card = (thread: Thread) => (
    <DocumentCommentThread
      key={thread.id}
      thread={thread}
      artifactId={artifactId}
      inDocument={inDocument(thread)}
      active={thread.id === activeThreadId}
      variant={presentation === "sheet" ? "sheet" : "column"}
      actions={comments}
      onActivate={() => onActivate(thread.id)}
      agentName={comments.agentName}
      sendToAgentAs={comments.sendToAgentAs}
    />
  );

  const shown =
    filter === "open" ? open : filter === "resolved" ? resolved : null;
  const empty =
    !draft &&
    comments.loaded &&
    (shown ?? comments.threads).length === 0 &&
    !(filter === "open" && resolved.length > 0);

  return (
    <>
      <div className="flex h-11 flex-none items-center gap-2 border-b border-[var(--fp-border)] pl-4 pr-2">
        <h2 className="flex items-baseline gap-2">
          <span className="text-[14px] font-semibold text-[var(--fp-ink)]">
            Comments
          </span>{" "}
          <span className="font-mono text-[12px] text-[#6E7480]">
            {comments.openCount}
          </span>
        </h2>
        <span className="flex-1" />
        {withFilter && (
          <IconButton
            label="Filter comments"
            expanded={filterAnchor !== null}
            onClick={(event) => setFilterAnchor(event.currentTarget)}
          >
            <ListFilter className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        )}
        <IconButton label="Close comments" onClick={onClose}>
          <X className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      </div>
      <div
        ref={listRef}
        className="fp-scroll-bare flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-2.5"
      >
        {draft && (
          <DocumentCommentComposer
            // Each comment its own composer: one started after another's
            // post failed starts with an empty field (no comment starts while
            // a post is out, so a pending post never loses its composer).
            key={draft.id}
            quote={draft.quote}
            onCancel={onCancelDraft}
            onSubmit={onSubmitDraft}
            sendToAgentAs={comments.sendToAgentAs}
          />
        )}
        {(shown ?? comments.threads).map(card)}
        {empty && (
          <div className="px-3.5 py-3 text-[13px] text-[#6E7480]">
            {filter === "resolved" ? "No resolved comments" : "No comments"}
          </div>
        )}
        {filter === "open" && resolved.length > 0 && (
          <>
            <span className="flex-1" />
            <button
              type="button"
              aria-expanded={resolvedOpen}
              onClick={() => setResolvedOpen((value) => !value)}
              className="flex w-full flex-none cursor-pointer items-center gap-2 rounded-[10px] px-3.5 py-2.5 text-left text-[#6E7480] hover:bg-[var(--fp-bg)]"
            >
              <CircleCheck className="h-[15px] w-[15px]" aria-hidden="true" />
              <span className="text-[13px]">Resolved</span>
              <span className="font-mono text-[12px]">{resolved.length}</span>
              <span className="flex-1" />
              <ChevronDown
                aria-hidden="true"
                className={`h-[15px] w-[15px] ${resolvedOpen ? "rotate-180" : ""}`}
              />
            </button>
            {resolvedOpen && resolved.map(card)}
          </>
        )}
      </div>
      <Menu
        anchorEl={filterAnchor}
        open={filterAnchor !== null}
        onClose={() => setFilterAnchor(null)}
        label="Show comments"
        items={(Object.keys(FILTER_LABELS) as Filter[]).map((value) => ({
          label: FILTER_LABELS[value],
          current: filter === value,
          onSelect: () => {
            setFilterAnchor(null);
            setFilter(value);
          },
        }))}
      />
    </>
  );
};

/** The phone sheet: a pushed state the system back dismisses. It portals
 *  into the drawer that hosts the pane when there is one, else to the page:
 *  outside the drawer, the drawer's focus trap takes the focus off the
 *  composer's field as it opens. */
const CommentsSheet: React.FC<DocumentCommentsPanelProps> = (props) => {
  const { onClose } = props;
  useDismissOnBack(true, onClose);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const anchor = useCallback((el: HTMLSpanElement | null) => {
    if (el)
      setHost(el.closest<HTMLElement>(".MuiDrawer-paper") ?? document.body);
  }, []);
  if (!host) return <span ref={anchor} hidden />;
  return createPortal(
    <div className="fixed inset-0 z-[1250]">
      <button
        type="button"
        aria-label="Close comments sheet"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-[rgba(23,25,28,0.28)]"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Comments"
        {...{ [COMMENTS_UI_ATTR]: "" }}
        className="absolute inset-x-0 bottom-0 flex max-h-[78vh] min-h-[50vh] flex-col overflow-hidden rounded-t-[16px] bg-[var(--fp-surface)] pb-[env(safe-area-inset-bottom,0px)] [box-shadow:0_-8px_30px_rgba(23,25,28,0.14)]"
      >
        <div className="flex flex-none justify-center pt-2">
          <span
            aria-hidden="true"
            className="h-1 w-9 rounded-[2px] bg-[rgba(23,25,28,0.2)]"
          />
        </div>
        <PanelBody {...props} withFilter={false} />
      </section>
    </div>,
    host
  );
};

const DocumentCommentsPanel: React.FC<DocumentCommentsPanelProps> = (props) => {
  if (props.presentation === "sheet") return <CommentsSheet {...props} />;
  return (
    <aside
      aria-label="Comments"
      {...{ [COMMENTS_UI_ATTR]: "" }}
      style={{ width: COMMENTS_COLUMN_PX }}
      className="flex h-full min-h-0 flex-none flex-col border-l border-[var(--fp-border)] bg-[#fbfaf8]"
    >
      <PanelBody {...props} withFilter />
    </aside>
  );
};

/** One thread beside its text while the column is closed (artboard 1):
 *  the column's card, in the editor's scroll container so it travels with
 *  the text. Escape, a click elsewhere, or opening the column dismisses
 *  it. */
export const DocumentCommentMarginCard: React.FC<{
  placement: MarginCardPlacement;
  artifactId: string;
  comments: UseDocumentComments;
  inDocument: (thread: Thread) => boolean;
  onDismiss: () => void;
}> = ({ placement, artifactId, comments, inDocument, onDismiss }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || ref.current?.contains(target)) return;
      // Another highlight shows its own card.
      if (target.closest("[data-comment-highlight]")) return;
      // The card's own menus and dialogs portal out of it. The overlay
      // drawer the document sits in is a modal too, but it holds the card:
      // a click on its text is a click elsewhere.
      const layer = target.closest(
        ".MuiModal-root, .MuiPopover-root, .MuiTooltip-popper"
      );
      if (layer && !(ref.current && layer.contains(ref.current))) return;
      onDismiss();
    };
    // Escape dismisses the card wherever the focus is (after a click on a
    // highlight it is in the editor, and the overlay drawer around the
    // editor stops the key on its way up), and is consumed: the pane's own
    // Escape stands down on a prevented event, so one press does not also
    // close the drawer. Capture phase, as the history column's Escape.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target instanceof Element ? event.target : null;
      // A menu or dialog the card opened holds the focus and keeps its own
      // Escape.
      if (target?.closest('[role="menu"], [role="listbox"], [role="dialog"]'))
        return;
      if (
        target instanceof HTMLTextAreaElement &&
        ref.current?.contains(target)
      ) {
        // The edit field's own Escape cancels the edit; the card stays.
        if (target.hasAttribute(COMMENT_EDIT_FIELD_ATTR)) return;
        // A reply with a draft keeps the card, or the draft would go with
        // it. The key is still consumed, so the drawer stays too.
        if (target.value.trim() !== "") {
          event.preventDefault();
          return;
        }
      }
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onDismiss]);
  const thread = comments.threads.find((t) => t.id === placement.threadId);
  if (!thread) return null;
  return createPortal(
    <div
      ref={ref}
      className="absolute z-20"
      {...{ [COMMENTS_UI_ATTR]: "" }}
      style={{ top: placement.top, left: placement.left }}
    >
      <DocumentCommentThread
        thread={thread}
        artifactId={artifactId}
        inDocument={inDocument(thread)}
        active
        variant="margin"
        actions={comments}
        onActivate={() => {}}
        agentName={comments.agentName}
        sendToAgentAs={comments.sendToAgentAs}
      />
    </div>,
    placement.container
  );
};

export default DocumentCommentsPanel;
